import { useEffect } from "react";
import { type KeywordFeedbackDetails } from "reedy-data/api";
import { Popover } from "~/components/ui/popover";
import type { KeywordFeedbackEntry } from "~/components/keywords-tab/hooks/use-keyword-feedback";
import { KeywordFeedbackCategoryPicker } from "./keyword-feedback-category-picker";
import { KeywordFeedbackFreeTextForm } from "./keyword-feedback-free-text-form";
import { KeywordFeedbackPopoverContent } from "./keyword-feedback-popover-content";
import { KeywordFeedbackPopoverTrigger } from "./keyword-feedback-popover-trigger";
import { KeywordFeedbackSubReasonPicker } from "./keyword-feedback-sub-reason-picker";
import { KeywordFeedbackSummary } from "./keyword-feedback-summary";
import { useKeywordFeedbackPopoverState } from "./use-keyword-feedback-popover-state";

const KEYWORDS_SCROLL_PARENT_SELECTOR = "[data-keywords-scroll-parent]";

interface KeywordFeedbackPopoverProps {
  keyword: string;
  entry: KeywordFeedbackEntry | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (details?: KeywordFeedbackDetails) => Promise<void>;
  onDiscard: () => Promise<void>;
}

export const KeywordFeedbackPopover = ({
  keyword,
  entry,
  isOpen,
  onOpenChange,
  onSubmit,
  onDiscard,
}: KeywordFeedbackPopoverProps) => {
  const state = useKeywordFeedbackPopoverState({
    entry,
    isOpen,
    onOpenChange,
    onSubmit,
    onDiscard,
  });

  // The keywords table scrolls inside an .overflow-auto wrapper; without
  // this the popover floats detached from its anchor when the row scrolls
  // past. Dismissing on scroll keeps the UI tidy and avoids portal-clipping
  // edge cases.
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => onOpenChange(false);
    const scrollParent = document.querySelector(
      KEYWORDS_SCROLL_PARENT_SELECTOR,
    );
    scrollParent?.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollParent?.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen, onOpenChange]);

  return (
    <Popover open={isOpen} onOpenChange={state.onOpenChange}>
      <KeywordFeedbackPopoverTrigger
        keyword={keyword}
        isFlagged={state.isFlagged}
        tooltipText={state.trigger.tooltipText}
        ariaText={state.trigger.ariaText}
        onClick={state.trigger.onClick}
      />

      <KeywordFeedbackPopoverContent
        title={state.header.title}
        subtitle={state.header.subtitle}
      >
        {state.view.kind === "summary" && (
          <KeywordFeedbackSummary
            keyword={keyword}
            details={state.view.details}
            onEdit={state.onEdit}
            onRemove={state.onRemove}
          />
        )}

        {state.view.kind === "categoryPicker" && (
          <KeywordFeedbackCategoryPicker
            hoveredCategory={state.view.hoveredCategory}
            onHover={state.onHoverCategory}
            onSelect={state.onCategoryClick}
          />
        )}

        {state.view.kind === "subReasonPicker" && (
          <KeywordFeedbackSubReasonPicker
            category={state.view.category}
            onSelect={state.onSubCategoryClick}
            onOther={state.onSubOtherSelect}
            onBack={state.onBack}
          />
        )}

        {state.view.kind === "freeTextForm" && (
          <KeywordFeedbackFreeTextForm
            value={state.view.freeText}
            onChange={state.onFreeTextChange}
            onSubmit={state.onFreeTextSubmit}
            onBack={state.onBack}
          />
        )}
      </KeywordFeedbackPopoverContent>
    </Popover>
  );
};
